// Copyright (c) 2026 Query Health Inc. All rights reserved.
// Author: Shubham Dixit <shubham@quantinohealth.com>
// Licensed under a Commercial License. See LICENSE for terms.

import { CheckCircle2 } from 'lucide-react'
import { cn } from '@/lib/utils'

// Deterministic, on-brand cartoon avatar (inline SVG — no external image, CSP-safe).
// Same seed always renders the same face; used for every person (patient / provider / user).
const AVATAR_GRADIENTS: [string, string][] = [
  ['#006EF9', '#02C7C3'],
  ['#02C7C3', '#22D3A8'],
  ['#6366F1', '#006EF9'],
  ['#0EA5E9', '#02C7C3'],
  ['#8B5CF6', '#006EF9'],
  ['#06B6D4', '#3B82F6'],
]
const SKIN_TONES = ['#F2C9A0', '#E8B48C', '#D49A6A', '#C68642', '#8D5524']
const HAIR_COLORS = ['#2C1B14', '#4A2C1A', '#6B4423', '#1F2937', '#9CA3AF']

function hashSeed(s: string): number {
  let h = 0
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0
  return h
}

interface PersonAvatarProps {
  /** Stable identity — patientId, particlePatientId, uid, email, or name. */
  seed: string
  /** Square size in px. */
  size?: number
  /** Show a ring around the avatar. */
  ring?: boolean
  /** Ring color class (e.g. 'ring-white/30' on dark banners). */
  ringClassName?: string
  /** Teal ring + checkmark badge (selection state). */
  selected?: boolean
  className?: string
}

export function PersonAvatar({
  seed,
  size = 44,
  ring = true,
  ringClassName = 'ring-border',
  selected = false,
  className,
}: PersonAvatarProps) {
  const h = hashSeed(seed || 'qh')
  const [g1, g2] = AVATAR_GRADIENTS[h % AVATAR_GRADIENTS.length]!
  const skin = SKIN_TONES[(h >>> 3) % SKIN_TONES.length]!
  const hair = HAIR_COLORS[(h >>> 6) % HAIR_COLORS.length]!
  const hairStyle = (h >>> 9) % 4
  const gid = `pa-${h.toString(36)}`

  return (
    <div className={cn('relative shrink-0', className)} style={{ width: size, height: size }}>
      <div
        className={cn(
          'h-full w-full rounded-full overflow-hidden',
          ring && 'ring-2',
          ring && (selected ? 'ring-[#02C7C3]' : ringClassName),
        )}
      >
        <svg viewBox="0 0 48 48" className="h-full w-full" aria-hidden="true">
          <defs>
            <linearGradient id={gid} x1="0" y1="0" x2="1" y2="1">
              <stop offset="0%" stopColor={g1} />
              <stop offset="100%" stopColor={g2} />
            </linearGradient>
          </defs>
          <rect width="48" height="48" fill={`url(#${gid})`} />
          {/* shoulders */}
          <path d="M9 48c0-7.5 6.7-12 15-12s15 4.5 15 12z" fill="#FFFFFF" opacity="0.92" />
          {/* head */}
          <circle cx="24" cy="21" r="10" fill={skin} />
          {/* hair variants */}
          {hairStyle === 0 && (
            <path d="M14 20c0-6 4.5-10 10-10s10 4 10 10c-2-3-5-4-10-4s-8 1-10 4z" fill={hair} />
          )}
          {hairStyle === 1 && (
            <path d="M13.5 22c-1-7 4-12 10.5-12s11.5 5 10.5 12c-1-2-1.5-5-3-6 0 2-1 3-2 3 0-2-1-4-2-4-2 2-9 2-12 1-1 2-1.5 4-2 6z" fill={hair} />
          )}
          {hairStyle === 2 && (
            <>
              <circle cx="24" cy="9" r="4" fill={hair} />
              <path d="M14 20c0-6 4.5-10 10-10s10 4 10 10c-2-4-5-5-10-5s-8 1-10 5z" fill={hair} />
            </>
          )}
          {hairStyle === 3 && (
            <path d="M14 19c0-5 4-9 10-9s10 4 10 9c-1.5-2-3-3-4-2.5 0 0-1 8.5-6 8.5s-6-8.5-6-8.5C16.8 16 15.5 17 14 19z" fill={hair} />
          )}
          {/* eyes */}
          <circle cx="20" cy="21" r="1.4" fill="#2A2A2A" />
          <circle cx="28" cy="21" r="1.4" fill="#2A2A2A" />
          {/* smile */}
          <path d="M20.5 25.5c1 1.6 6 1.6 7 0" stroke="#9A4B2E" strokeWidth="1.4" strokeLinecap="round" fill="none" />
        </svg>
      </div>
      {selected && (
        <span className="absolute -bottom-0.5 -right-0.5 h-4 w-4 rounded-full bg-[#02C7C3] flex items-center justify-center ring-2 ring-card">
          <CheckCircle2 className="h-3 w-3 text-white" />
        </span>
      )}
    </div>
  )
}
